import React, { useEffect, useState } from 'react';
import {
    GridCellParams,
    useGridApiRef,
    GridSelectionModel,
    GridSortModel,
    GridFilterModel,
    GridColumnResizeParams,
    GridColumnOrderChangeParams,
} from '@mui/x-data-grid-pro';

import { GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid';

import { SearchTab, InventoryList } from "../../model/Inventory";

import {
    CartItem,
    CartItemIdentifier,
    CartList,
    useGetCartQuery,
    useGetItemsBasicSearchQuery,
    useGetUserInfoQuery,
    useGetItemsAdvancedSearchQuery,
    useGetItemByIdQuery
} from "../../services/grace";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import CartItemDialog from "../../components/dialogs/CartItemDialog/CartItemDialog";
import InventoryGrid from "../../components/grids/InventoryGrid/InventoryGrid";
import { Box, CircularProgress, Dialog, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../util/hooks";
import { RootState } from "../../util/store";
import {
    setFilterModel,
    setCurrentPage,
    setPageSize,
    setInventoryData,
    setSelectionModel,
    setSortModel,
    setColumnDef,
    setInventoryDataRetrievedTS,
} from "../../features/inventory/inventorySlice";
import {useAlertDispatch} from '../../context/AlertContext/AlertContext';
import GDGAppBar from "../../components/app/GDGAppBar/GDGAppBar";
import GDGAppBarButton from "../../components/app/GDGAppBar/GDGAppBarButton";
import { useHistory } from "react-router-dom";
import { GridColumnDef } from "../../components/grids/grid";
import { format } from "date-fns";

enum PageModes {
    List = "list",
    AddToCart = "addToCart",
}

declare type PageMode = PageModes.List | PageModes.AddToCart;

export default function Inventory(): JSX.Element {
    const apiRef = useGridApiRef();
    const history = useHistory();
    const dispatch = useAppDispatch();

    const { data: userData } = useGetUserInfoQuery();
    const [pageMode, setPageMode] = useState<PageMode>(PageModes.List);

    const inventoryData = useAppSelector((state: RootState) => state.inventory.inventoryData);
    const inventoryDataRetrievedTS = useAppSelector((state: RootState) => state.inventory.inventoryDataRetrievedTS);
    const selectionModel = useAppSelector((state: RootState) => state.inventory.selectionModel);
    const filterModel = useAppSelector((state: RootState) => state.inventory.filterModel);
    const sortModel = useAppSelector((state: RootState) => state.inventory.sortModel);
    const basicSearch = useAppSelector((state: RootState) => state.inventory.basicSearch);
    const advancedSearch = useAppSelector((state: RootState) => state.inventory.advancedSearch);
    const searchType = useAppSelector((state: RootState) => state.inventory.searchType);
    const currentPage = useAppSelector((state: RootState) => state.inventory.currentPage);
    const pageSize = useAppSelector((state: RootState) => state.inventory.pageSize);
    const columnDef = useAppSelector((state: RootState) => state.inventory.columnDef);

    const [itemId, setItemId] = useState<string>("");
    const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
    const [addCartItems, setAddCartItems] = useState<Array<CartItemIdentifier>>([]);

    const alertDispatch = useAlertDispatch();

    // Load the inventory grid using data generated from submitting either
    // the basic search parameters or the advanced search parameters
    const {
        data: basicData,
        refetch: basicRefetch,
        error: basicError,
        isError: basicIsError,
        isFetching: basicIsFetching,
    } = useGetItemsBasicSearchQuery(basicSearch.params, { skip: !basicSearch.active });

    const {
        data: advancedData,
        refetch: advancedRefetch,
        error: advancedError,
        isError: advancedIsError,
        isFetching: advancedIsFetching
    } = useGetItemsAdvancedSearchQuery(advancedSearch.params, { skip: !advancedSearch.active });

    // Only load the cart if the user has access to it
    const loadCart = userData === undefined ? false : (
        userData.permissions.orderItems
        || userData.permissions.orderMaterials
        || userData.permissions.orderServices
    );

    // Errors for cart data are handled in AppDrawer.tsx
    const {
        data: cartQueryData,
        isFetching: cartIsFetching,
    } = useGetCartQuery(null, { skip: !loadCart });

    const [cartData, setCartData] = useState<CartList>([]);

    useEffect(() => {
        if (cartQueryData === undefined) {
            setCartData([]);
        } else {
            setCartData(cartQueryData);
        }
    }, [cartQueryData, cartIsFetching]);

    useEffect(() => {
        if (basicIsFetching) {
            dispatch(setInventoryData([]));
            dispatch(setInventoryDataRetrievedTS(""));
        } else if (basicIsError) {
            const err = basicError as FetchBaseQueryError;
            console.log(err);

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "general",
                        message: "Error retrieving inventory data - please try again",
                    }
                });
            }
        } else if (basicData !== undefined) {
            dispatch(setInventoryDataRetrievedTS(format(new Date(), "dd/MM/yyyy HH:mm:ss")));

            const _basicData: InventoryList = JSON.parse(JSON.stringify(basicData));
            if(_basicData.length > 0){                
                for(let i = 0; i < _basicData.length; i++){
                    if(cartData.find((item: CartItem) => item.itemId === _basicData[i]["id"])){
                        _basicData[i]["cartStatus"] = "1";
                    }
                    else{
                        _basicData[i]["cartStatus"] = "";
                    }
                } 
            }
            dispatch(setInventoryData(_basicData));
        }
    }, [cartData,
        dispatch,
        basicData,
        basicIsFetching,
        basicIsError,
        basicError,
        alertDispatch
    ]
    );

    useEffect(() => {
        if (advancedIsFetching) {
            dispatch(setInventoryData([]));
            dispatch(setInventoryDataRetrievedTS(""));
        } else if (advancedIsError) {
            const err = advancedError as FetchBaseQueryError;
            console.log(err);

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "general",
                        message: "Error retrieving inventory data - please try again",
                    }
                });
            }
        } else if (advancedData !== undefined) {
            dispatch(setInventoryDataRetrievedTS(format(new Date(), "dd/MM/yyyy HH:mm:ss")));
            dispatch(setInventoryData(advancedData));
        }
    }, [
        dispatch,
        advancedData,
        advancedIsFetching,
        advancedIsError,
        advancedError,
        alertDispatch
    ]
    );

    // We preload the item data so that there's not a blank screen
    // shown in between the click on "View" or the barcode and
    // the display of the view/edit page.
    // -- START PRELOAD --
    const {
        data: itemData,
        error: itemError,
        isFetching: itemIsFetching,
        isError: itemIsError,
    } = useGetItemByIdQuery(itemId, {skip: itemId === ""});

    useEffect(() => {
        if (itemIsFetching) {
            setShowLoadingOverlay(true);
        } else if (!itemIsFetching && itemData !== undefined) {
            setShowLoadingOverlay(false);
            const type = itemData.type === "container" ? "carton" : itemData.type;
            history.push(`/inventory/edit/${type}/${itemId}`)
        } else if (itemIsError) {
            setShowLoadingOverlay(false);
            const err = itemError as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        open: true,
                        type: "general",
                        message: "Error retrieving item data - please try again",
                    }
                });
            }
        }
    }, [itemId, itemData, itemIsFetching, itemIsError, itemError, alertDispatch, history]);
    // -- END PRELOAD --

    const handleCellClick = (params: GridCellParams, event: React.MouseEvent) => {
        if (params.colDef.field === 'view') {
            event.stopPropagation();
            setItemId(params.id.toString());
        } else if (params.colDef.field === 'scanId') {
            event.stopPropagation();
            if (params.value !== undefined) {
                window.open(`/api/services/link/${params.value}`)
            }
        } else if (params.colDef.field === 'cartStatus') {
            event.stopPropagation();
            if (userData?.permissions.orderItems) {
                if(params.row.status !== 'destroyed' && params.row.status !== 'permout'){
                    const target = cartData.find((item: CartItem) => item.itemId === params.id);

                    // Only add items that aren't in the cart already
                    if (target === undefined) {
                        const type = params.row['type'];
                        const status = params.row['status'];
                        setAddCartItems([{
                            objectCode: params.id.toString(),
                            objectType: type === undefined || type === null ? '' : type.toString(),
                            objectStatus: status === undefined || status === null ? '' : status.toString(),
                        }]);

                        setPageMode(PageModes.AddToCart);
                    }
                }
            }
        } else if (params.colDef.field === GRID_CHECKBOX_SELECTION_FIELD) {
            event.stopPropagation();
        } else {
            // Toggle the item by either adding or removing it from the selection model
            const index = selectionModel.indexOf(params.id);
            const model = [...selectionModel];
            if (index !== -1) {
                model.splice(index, 1);
            } else {
                model.push(params.id);
            }
            dispatch(setSelectionModel(model));
        }
    };

    const handleBarcodeClick = (event: React.MouseEvent, params: GridCellParams) => {
        event.stopPropagation();
        setItemId(params.id.toString());
    };

    const handleFilterModelChange = (model: GridFilterModel) => {
        dispatch(setFilterModel(model));
    };

    const handleSelectionModelChange = (model: GridSelectionModel) => {
        dispatch(setSelectionModel(model));
    };

    // @TODO can be optimised
    const handleSortModelChange = (model: GridSortModel) => {
        if ((sortModel.length === 0) || (model.length === 0)) {
            dispatch(setSortModel(model));
        } else if (model[0].field !== sortModel[0].field || model[0].sort !== sortModel[0].sort) {
            dispatch(setSortModel(model));
        }
    };

    const handlePageChange = (newPage: number) => {
        dispatch(setCurrentPage(newPage));
    };

    const handlePageSizeChange = (newPageSize: number) => {
        dispatch(setPageSize(newPageSize));
    }

    const handleInventorySearch = () => {
        history.push("/search");
    };

    const handleCartAddItemsClose = () => {
        setAddCartItems([]);
        setPageMode(PageModes.List);
    };

    const handleInventoryAddToCart = () => {
        if (selectionModel.length !== 0) {
            const items: Array<CartItemIdentifier> = [];

            selectionModel.forEach((item) => {
                const type = apiRef.current.getCellValue(item, 'type');
                const status = apiRef.current.getCellValue(item, 'status');
                items.push({
                    objectCode: item.toString(),
                    objectType: type === undefined || type === null ? '' : type.toString(),
                    objectStatus: status === undefined || status === null ? '' : status.toString(),
                })
            });
            setAddCartItems(items);
            setPageMode(PageModes.AddToCart);
        }
    };

    const handleInventoryClear = () => {
        dispatch(setInventoryData([]));
        dispatch(setSelectionModel([]));
    }

    const handleInventoryReload = () => {
        if (searchType === SearchTab.basic) {
            basicRefetch();
        } else {
            advancedRefetch();
        }
        // Clear the selection model because reloading data doesn't do this
        dispatch(setSelectionModel([]));
    };

    const handleColumnResize = (params: GridColumnResizeParams) => {
        const newColumnDef:GridColumnDef[] = [];
        columnDef.forEach((item) => {
            if (item.field === params.colDef.field) {
                newColumnDef.push({...item, width: params.width});
            } else {
                newColumnDef.push({...item});
            }
        });
        dispatch(setColumnDef(newColumnDef));
    };

    // Indexes are minus one due to the column definition not
    // containing the automatically added checkbox column
    const handleColumnReorder = (params: GridColumnOrderChangeParams) => {
        const newColumnDef = [...columnDef];
        const item = newColumnDef.splice(params.oldIndex - 1, 1);
        newColumnDef.splice(params.targetIndex - 1, 0, item[0])
        dispatch(setColumnDef(newColumnDef));
    };

    return (
        <Box sx={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <GDGAppBar title='Inventory'>
                {userData?.permissions.addContainer &&
                    <Box>
                        <GDGAppBarButton
                            onClick={() => history.push(`/inventory/add/carton`)}
                            text='+ Add Carton'
                        />
                    </Box>
                }
                {userData?.permissions.addFilefolder &&
                    <Box>
                        <GDGAppBarButton
                            onClick={() => history.push(`/inventory/add/file`)}
                            text='+ Add File'
                        />
                    </Box>
                }
                {userData?.permissions.addTape &&
                    <Box>
                        <GDGAppBarButton
                            onClick={() => history.push(`/inventory/add/tape`)}
                            text='+ Add Tape'
                        />
                    </Box>
                }
                {userData?.permissions.orderItems &&
                    <GDGAppBarButton
                        onClick={handleInventoryAddToCart}
                        text='+ Add to Cart'
                    />
                }

                <GDGAppBarButton
                    onClick={handleInventoryClear}
                    text='Clear'
                />

                <GDGAppBarButton
                    onClick={handleInventoryReload}
                    text='Reload'
                />

                <GDGAppBarButton
                    onClick={handleInventorySearch}
                    text='Search'
                    icon={['far', 'search']}
                />
            </GDGAppBar>

            {pageMode === PageModes.AddToCart &&
                <CartItemDialog
                    open={pageMode === PageModes.AddToCart}
                    type="item"
                    mode="add"
                    cartItems={addCartItems}
                    itemTypes={userData?.serviceTypes !== undefined ? userData?.serviceTypes : []}
                    onClose={handleCartAddItemsClose}
                    onSubmit={handleCartAddItemsClose}
                />
            }

            <Dialog
                open={showLoadingOverlay}
                maxWidth="sm"
                sx={{justifyContent: "center", alignContent: "center"}}
                onClose={() => {return}} // Prevent closing
            >
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    padding: 1,
                    margin: 1,
                    width: 200,
                    height: 200,
                    justifyContent: "center",
                    alignItems: "center"}}
                >
                    <Typography sx={{padding: 1}}>Loading</Typography>
                    <CircularProgress/>
                </Box>
            </Dialog>

            <InventoryGrid
                apiRef={apiRef}
                loading={basicIsFetching || advancedIsFetching || cartIsFetching}
                rows={inventoryData}
                retrievedTS={inventoryDataRetrievedTS}
                columnDef={columnDef}
                cartData={cartData}
                fullResults={(basicSearch.active && basicSearch.params.fullResults) || advancedSearch.active}
                filterModel={filterModel}
                onFilterModelChange={handleFilterModelChange}
                selectionModel={selectionModel}
                onSelectionModelChange={handleSelectionModelChange}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                onCellClick={handleCellClick}
                onBarcodeClick={handleBarcodeClick}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onColumnResize={handleColumnResize}
                onColumnOrderChange={handleColumnReorder}
                page={currentPage}
                pageSize={pageSize}
                disableSelectionOnClick
            />
        </Box>
    );
}
